import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Flame, Trophy, Star, CheckCircle2, ArrowRight, Award, Users, Quote, Loader2, PlayCircle, Dumbbell, Sparkles, Salad, RotateCcw, History, ExternalLink } from 'lucide-react';
import { progressAPI, leaderboardAPI, coachAPI, videosAPI, contentAPI, mediaUrl } from '../services/api';
import { ValidateDialog } from '../components/ValidateDialog';
import { BadgeIcon } from '../components/BadgeIcon';
import { VideoView } from '../components/VideoView';
import { NextGiftCard } from '../components/NextGiftCard';
import { RoutineStats } from '../components/RoutineStats';

const URL_RE = /(https?:\/\/[^\s]+)/g;
const Linkified = ({ text }) => (
  <>
    {String(text).split(URL_RE).map((part, i) =>
      /^https?:\/\//.test(part) ? (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-orange-500 font-semibold underline break-all hover:text-orange-600">{part}</a>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    )}
  </>
);

const Stat = ({ icon: Icon, value, label, color, testId }) => (
  <div className="bg-white dark:bg-red-950/40 rounded-2xl p-4 border border-red-100 dark:border-red-900/50" data-testid={testId}>
    <Icon className={`w-5 h-5 ${color}`} />
    <p className="text-2xl font-extrabold text-red-950 dark:text-red-50 mt-2">{value}</p>
    <p className="text-xs text-muted-foreground font-medium">{label}</p>
  </div>
);

export default function Today() {
  const [data, setData] = useState(null);
  const [top, setTop] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [videos, setVideos] = useState({ motivation: null, session: null, nutrition: null });
  const [secretContent, setSecretContent] = useState(null);
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useState(false);
  const [shine, setShine] = useState(false);

  const load = useCallback(() => {
    progressAPI.dashboard().then((d) => {
      setData(d);
      videosAPI.day(d.current_day).then(setVideos).catch(() => {});
      contentAPI.secret(d.today_challenge.secret_number).then(setSecretContent).catch(() => {});
    }).catch(() => {});
    leaderboardAPI.individual().then((r) => setTop(r.slice(0, 3))).catch(() => {});
    coachAPI.inbox().then(setInbox).catch(() => {});
    progressAPI.history().then(setHistory).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleValidated = () => {
    setShine(true);
    load();
    setTimeout(() => setShine(false), 1600);
  };

  if (!data) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-red-600" /></div>;
  }

  const { current_day, today_challenge, today_status, stats, badges, coach_message, name } = data;
  const validated = today_status === 'valide' || today_status === 'rattrape';
  const unlockedBadges = badges.filter((b) => b.unlocked);
  const firstName = (name || '').split(' ')[0];

  return (
    <div className="space-y-6 animate-rise">
      {/* Welcome */}
      <div>
        <p className="text-sm text-muted-foreground">Bonjour {firstName} 👋</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-red-950 dark:text-red-50">
          Jour {current_day} <span className="text-muted-foreground font-bold">/ 21</span>
        </h1>
      </div>

      {/* Progress bar */}
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-foreground">Ta progression globale</span>
          <span className="text-sm font-extrabold text-red-600" data-testid="progress-percent">{stats.progress_percent}%</span>
        </div>
        <Progress value={stats.progress_percent} className="h-3" />
        <p className="text-xs text-muted-foreground mt-2">{stats.validated_count} défis validés sur 21</p>
      </Card>

      {/* Challenge history (last 6 months) */}
      {history.length > 0 && (
        <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50" data-testid="challenge-history">
          <h3 className="font-bold text-red-950 dark:text-red-50 mb-1 flex items-center gap-2"><History className="w-5 h-5 text-orange-500" />Mes challenges précédents</h3>
          <p className="text-xs text-muted-foreground mb-3">Tes progressions atteintes sur les 6 derniers mois.</p>
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} className="flex items-center gap-3" data-testid={`history-${h.id}`}>
                <span className="text-xs text-muted-foreground w-24 shrink-0">{(h.cycle_start_date || h.archived_at || '').slice(0, 10)}</span>
                <div className="flex-1">
                  <Progress value={h.progress_percent} className="h-2" indicatorClassName="bg-orange-500" />
                </div>
                <span className="text-sm font-extrabold text-orange-500 w-12 text-right">{h.progress_percent}%</span>
                <span className="text-xs text-muted-foreground w-16 text-right">{h.validated_count}/21</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Next gift progression (Top 10) */}
      <NextGiftCard />

      {/* Personal routine statistics */}
      <RoutineStats />

      {/* Coach message */}
      <Card className="p-5 rounded-2xl bfc-gradient text-white border-0 relative overflow-hidden" data-testid="coach-message">
        <Quote className="w-8 h-8 text-white/30 mb-2" />
        <p className="font-semibold leading-relaxed">{coach_message}</p>
        {inbox[0] && (
          <p className="text-sm text-red-50 mt-3 pt-3 border-t border-white/20">
            📣 {inbox[0].coach_name} : {inbox[0].content}
          </p>
        )}
      </Card>

      {/* Secret du moment */}
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50" data-testid="secret-of-moment">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-red-600" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-red-700/80 dark:text-red-400">Secret {today_challenge.secret_number} du moment</span>
        </div>
        <h3 className="font-bold text-red-950 dark:text-red-50 mb-3">{today_challenge.secret}</h3>
        {secretContent && (secretContent.title || secretContent.text || (secretContent.images && secretContent.images.length > 0) || (secretContent.links && secretContent.links.length > 0)) && (
          <div>
            {secretContent.title && <p className="font-semibold text-foreground mb-1">{secretContent.title}</p>}
            {secretContent.text && <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed"><Linkified text={secretContent.text} /></p>}
            {secretContent.links?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="secret-links">
                {secretContent.links.map((lnk, i) => (
                  <a key={i} href={lnk.url} target="_blank" rel="noopener noreferrer" data-testid={`secret-link-${i}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500/10 text-orange-600 dark:text-orange-400 text-xs font-semibold border border-orange-500/20 hover:bg-orange-500/20 transition-colors">
                    <ExternalLink className="w-3.5 h-3.5" />{lnk.label || lnk.url}
                  </a>
                ))}
              </div>
            )}
            {secretContent.images?.length > 0 && (
              <div className="flex gap-2 overflow-x-auto no-scrollbar mt-3">
                {secretContent.images.map((img, i) => (
                  <img key={i} src={mediaUrl(img)} alt="" className="h-28 rounded-lg object-cover shrink-0" />
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Nutrition formation video */}
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50" data-testid="nutrition-video-card">
        <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-foreground">
          <Salad className="w-4 h-4 text-green-500" /> Ta vidéo de formation nutrition
        </div>
        <div data-testid="nutrition-video">
          {videos.nutrition?.url ? (
            <VideoView video={videos.nutrition} />
          ) : (
            <div className="w-full aspect-video flex flex-col items-center justify-center bfc-dark text-white gap-2 rounded-xl">
              <PlayCircle className="w-12 h-12 text-orange-500" />
              <p className="text-xs text-neutral-300">Formation nutrition bientôt disponible</p>
            </div>
          )}
        </div>
      </Card>

      {/* Séance de sport */}
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50" data-testid="session-video-card">
        <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-foreground">
          <Dumbbell className="w-4 h-4 text-orange-500" /> Ta séance de sport
          <Link to="/seances" data-testid="today-see-seances" className="ml-auto text-xs text-orange-500 font-semibold flex items-center gap-1">Toutes les séances <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
        <div data-testid="session-video">
          {videos.session?.url ? (
            <VideoView video={videos.session} />
          ) : (
            <div className="w-full aspect-video flex flex-col items-center justify-center bfc-dark text-white gap-2 rounded-xl">
              <PlayCircle className="w-12 h-12 text-orange-500" />
              <p className="text-xs text-neutral-300">Séance du jour bientôt disponible</p>
            </div>
          )}
        </div>
      </Card>

      {/* Motivation video */}
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50" data-testid="motivation-video-card">
        <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-foreground">
          <Flame className="w-4 h-4 text-orange-500" /> Ta vidéo de motivation
        </div>
        <div data-testid="motivation-video">
          {videos.motivation?.url ? (
            <VideoView video={videos.motivation} />
          ) : (
            <div className="w-full aspect-video flex flex-col items-center justify-center bfc-dark text-white gap-2 rounded-xl">
              <PlayCircle className="w-12 h-12 text-orange-500" />
              <p className="text-xs text-neutral-300">Vidéo de motivation bientôt disponible</p>
            </div>
          )}
        </div>
      </Card>

      {/* Today challenge */}
      <Card className={`p-6 rounded-2xl border-2 border-red-200 dark:border-red-800 shadow-sm relative overflow-hidden ${shine ? 'bfc-glow-pulse' : ''}`} data-testid="today-challenge-card">
        {shine && <div className="bfc-shine-layer" data-testid="validation-shine" />}
        <div className="flex items-center justify-between mb-3">
          <Badge className="bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-100 rounded-full">Secret {today_challenge.secret_number} · {today_challenge.secret}</Badge>
          {validated ? (
            <Badge className="bg-red-600 text-white"><CheckCircle2 className="w-3 h-3 mr-1" />Validé</Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-700 border-amber-200">À faire</Badge>
          )}
        </div>
        <h2 className="text-xl font-bold text-red-950 dark:text-red-50">{today_challenge.title}</h2>
        <div className="mt-3 space-y-1.5 text-sm">
          <p className="text-foreground"><span className="font-semibold text-red-700 dark:text-red-300">Débutant :</span> {today_challenge.beginner_version}</p>
          <p className="text-foreground"><span className="font-semibold text-rose-700 dark:text-rose-300">Avancé :</span> {today_challenge.advanced_version}</p>
          <p className="text-muted-foreground pt-1">🥗 {today_challenge.nutrition_action}</p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          data-testid="open-validate-button"
          className={`w-full mt-5 rounded-xl py-6 font-bold text-base ${validated ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-200' : 'bg-orange-500 hover:bg-orange-600 text-white'}`}
        >
          {validated ? 'Revoir / mettre à jour mon défi' : 'Voir & valider mon défi du jour'}
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Stat icon={Star} value={stats.total_points} label="Points" color="text-orange-500" testId="stat-points" />
        <Stat icon={RotateCcw} value={stats.catch_up_count ?? 0} label="Défis à rattraper" color="text-red-500" testId="stat-catchup" />
        <Stat icon={Award} value={stats.badges_count} label="Badges" color="text-red-600" testId="stat-badges" />
      </div>

      {/* Badges preview */}
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-red-950 dark:text-red-50">Mes badges</h3>
          <Link to="/progres" className="text-sm text-red-600 font-semibold">Tout voir</Link>
        </div>
        {unlockedBadges.length === 0 ? (
          <p className="text-sm text-muted-foreground">Valide ton premier défi pour débloquer un badge !</p>
        ) : (
          <div className="flex gap-3 overflow-x-auto no-scrollbar">
            {unlockedBadges.map((b) => (
              <div key={b.badge_id} className="flex flex-col items-center gap-1.5 shrink-0 w-20" data-testid={`badge-${b.badge_id}`}>
                <div className="w-14 h-14 rounded-2xl bfc-gradient-warm flex items-center justify-center text-white animate-pop">
                  <BadgeIcon name={b.icon} className="w-7 h-7" />
                </div>
                <span className="text-[11px] text-center font-medium text-foreground leading-tight">{b.name}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Leaderboard preview */}
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-red-950 dark:text-red-50 flex items-center gap-2"><Trophy className="w-5 h-5 text-orange-500" />Classement</h3>
          <Link to="/classement" data-testid="today-see-leaderboard" className="text-sm text-orange-500 font-semibold flex items-center gap-1">Voir le Top 10 <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="space-y-2">
          {top.map((u) => (
            <div key={u.user_id} className={`flex items-center gap-3 p-2 rounded-xl ${u.is_me ? 'bg-red-50 dark:bg-red-900/30' : ''}`}>
              <span className="w-6 text-center font-extrabold text-muted-foreground">{u.rank}</span>
              <span className="flex-1 font-semibold text-foreground text-sm">{u.name}{u.is_me && ' (toi)'}</span>
              <span className="text-sm font-extrabold text-orange-500">{u.total_points} pts</span>
            </div>
          ))}
        </div>
      </Card>

      <ValidateDialog open={open} onOpenChange={setOpen} challenge={today_challenge} onValidated={handleValidated} />
    </div>
  );
}
